import { Link } from 'react-router-dom'
import { Facebook, Instagram, Mail, MapPin, RotateCcw } from 'lucide-react'
import { useCookieConsent } from '../cookie/CookieConsentContext'
import { useContactModal } from '../ContactModalContext'

const footerLinks = {
  servizi: [
    { label: 'Luce e Gas', href: 'https://compare.bollettaamica.com/home/energia', external: true },
    { label: 'Fibra', href: 'https://compare.bollettaamica.com/home/fibra', external: true },
    { label: 'Streaming', href: 'https://compare.bollettaamica.com/home/streaming', external: true },
  ],
  azienda: [
    { label: 'Il blog di Carletto', href: '/il-blog-di-carletto' },
    { label: 'Chi siamo', href: '/#chi-siamo' },
    { label: 'FAQ', href: '/#faq' },
    { label: 'Lavora con noi', href: '/lavora-con-noi' },
    { label: 'ItalConnect', href: 'https://ital-connect.it', external: true },
  ],
  legale: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Cookie Policy', href: '/cookie' },
    { label: 'Termini e Condizioni', href: '/termini-e-condizioni-generali-duso' },
    { label: 'Privacy Clienti e Fornitori', href: '/informativa-privacy-clienti-e-fornitori' },
  ],
}

const socialLinks = [
  { icon: Facebook, href: 'https://www.facebook.com/bollettaamica', label: 'Facebook' },
  { icon: Instagram, href: 'https://www.instagram.com/bollettaamica', label: 'Instagram' },
]

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const { resetConsent } = useCookieConsent()
  const { openModal } = useContactModal()

  return (
    <footer className="bg-dark-800 text-white">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img
                src="/logo_white.svg"
                alt="Bolletta Amica"
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-dark-300 mb-6 max-w-sm">
              Confronta e risparmia sulle tue bollette di luce, gas, fibra e streaming. 
              Carletto ti aiuta a trovare l'offerta migliore per te!
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-dark-700 flex items-center justify-center hover:bg-primary hover:text-dark-900 transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Servizi */}
          <div>
            <h3 className="font-display font-semibold text-lg mb-4">Servizi</h3>
            <ul className="space-y-3">
              {footerLinks.servizi.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dark-300 hover:text-primary transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-dark-300 hover:text-primary transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Azienda */}
          <div>
            <h3 className="font-display font-semibold text-lg mb-4">Azienda</h3>
            <ul className="space-y-3">
              {footerLinks.azienda.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dark-300 hover:text-primary transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-dark-300 hover:text-primary transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legale */}
          <div>
            <h3 className="font-display font-semibold text-lg mb-4">Legale</h3>
            <ul className="space-y-3">
              {footerLinks.legale.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-dark-300 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  onClick={resetConsent}
                  className="text-dark-300 hover:text-primary transition-colors flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Gestisci Cookie
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-12 pt-8 border-t border-dark-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-dark-700/50 rounded-xl p-5 border border-dark-600 hover:border-primary/30 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-dark-400 text-sm mb-1">Sede legale</p>
                  <p className="text-white font-medium">ItalConnect S.r.l.</p>
                  <p className="text-dark-300 text-sm">Via Alessandro Volta 24, 20099 Sesto San Giovanni (MI)</p>
                </div>
              </div>
            </div>
            <div className="bg-dark-700/50 rounded-xl p-5 border border-dark-600 hover:border-primary/30 transition-colors">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-dark-400 text-sm mb-1">Contattaci</p>
                  <button 
                    onClick={() => openModal()}
                    className="text-white font-medium hover:text-primary transition-colors"
                  >
                    Scrivici un messaggio
                  </button>
                  <p className="text-dark-300 text-sm mt-1">Rispondiamo entro 24-48 ore</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-dark-700">
        <div className="container-custom py-6">
          <div className="flex flex-col gap-4">
            <p className="text-dark-400 text-sm text-center">
              &copy; {currentYear} Bolletta Amica - Bolletta Amica è un servizio offerto da ItalConnect S.r.l. - P.IVA 10663330966
            </p>
            <p className="text-dark-500 text-xs text-center">
              ItalConnect S.r.l. aderisce al codice di condotta AGCOM (DELIBERA N. 197/23/CONS) con protocollo numero 0067534.
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
